import { defineComponent as _defineComponent } from 'vue'
import {
  computed,
  getCurrentInstance,
  onBeforeUnmount,
  onMounted,
  ref,
} from 'vue';

import { getAppVariable } from '@white-label-helper/get-app-variable';
import {
  US_PHONE_DEFAULT_FORMAT,
  PHONE_DEFAULT_FORMAT,
} from '@white-label-configuration/constants';

// Types
import type { TranslateResult } from 'vue-i18n';
import type { ValidationState } from '@white-label-types/vue-plugins';

// @ts-ignore
import VuePhoneNumberInput from 'vue-phone-number-input';

interface VuelidateState extends ValidationState {
  onlyNumbers?: boolean;
  minLength?: boolean;
  maxLength?: boolean;
  required?: boolean;
}

type PhoneFieldEvent = {
  /**
   * Dialing code
   * @example "421"
   */
  countryCallingCode: string;
  /**
   * ISO
   * @example "SK"
   */
  countryCode: string;
  /** @example "+421987609783" */
  e164: string;
  /** @example "+421 987 609 783" */
  formatInternational: string;
  /** @example "0987 609 783" */
  formatNational: string;
  /** @example "+421987609783" */
  formattedNumber: string;
  isValid: boolean;
  /** @example "987609783" */
  nationalNumber: string;
  /** @example "0987609783" */
  phoneNumber: string;
  type: undefined;
  /** @example "tel:+421987609873" */
  uri: string;
};

interface PhoneFieldValue {
  phone_number?: string
  formatted_number?: string
  country_code?: string
  country_iso_code?: string
}

interface Props {
  value: PhoneFieldValue
  error?: VuelidateState | Record<string, any>
  customErrorMessages?: Record<string, TranslateResult>
  label?: string
  addRequiredStar?: boolean
  disabled?: boolean
  isSuccess?: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'phone-field',
  props: {
    value: { default: () => ({
    phone_number: '',
    formatted_number: '',
    country_code: '',
    country_iso_code: '',
  }) },
    error: { default: () => ({}) },
    customErrorMessages: { default: () => ({}) },
    label: { default: '' },
    addRequiredStar: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    isSuccess: { type: Boolean, default: false }
  },
  emits: ["input", "blur"],
  setup(__props: any, { expose, emit }: { emit: ({
  (e: 'input', phoneFieldValue: PhoneFieldValue): void
  (e: 'blur'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value: PhoneFieldValue, error: VuelidateState | Record<string, any>, customErrorMessages: Record<string, TranslateResult>, label: string, addRequiredStar: boolean, disabled: boolean, isSuccess: boolean };





const instance = getCurrentInstance();
const proxy = instance?.proxy as Vue;

const placeholder = ref('');
const phoneField = ref<HTMLDivElement | null>(null);

/**
 * Get the airport country code from the partner config
 * @returns Airport country code
 */
const airportCountryCode = computed<string>(() => {
  try {
    const primaryProduct = getAppVariable('primary_product_type');

    //for standalone travel insurance use default_language because there's no POI
    if (primaryProduct === 'travel_insurance') {
      const defaultLang = getAppVariable('default_language');
      if (defaultLang) {
        const regionCode = defaultLang.split('-')[1];
        if (regionCode) {
          return regionCode;
        }
      }
    }

    //for other products, use POI country code
    return getAppVariable('poi.country.code');
  } catch(error) {
    return 'US';
  }
});

const isErrorExist = computed<boolean>(() => {
  return '$error' in props.error && props.error.$error;
});

const customErrorMessage = computed(() => {
  const errorState: string = getErrorState();
  return getErrorMessage(errorState);
});

const errorMessage = computed<string>(() => {
  if (customErrorMessage.value) {
    return customErrorMessage.value;
  }
  if ('required' in props.error && !props.error.required) {
    return `${proxy.$t(
      'UI.formErrors.pleaseEnter'
    )} ${props.label.toLowerCase()}.`;
  }

  return `${proxy.$t(
    'UI.formErrors.pleaseEnterValid'
  )} ${props.label.toLowerCase()}`;
});

/**
 * This had been done because:
 * 1. Requirement is need to have two different items in input for phone (country select, phone number)
 *    and each needed own focus/hover state.
 * 2. Library which had been used doesn't give easy access to event to focus/blur state
 *    and to setup custom attributes.
 *    That is why had been decided to write custom one.
 */
const customiseVuePhoneInputLibrary = (): void => {
  if (phoneField.value instanceof HTMLDivElement) {
    const inputTel = phoneField.value.querySelector('.input-tel input');
    const countrySelector = phoneField.value.querySelector(
      '.country-selector input'
    );

    if (inputTel instanceof HTMLElement) {
      inputTel.addEventListener('focus', callbackFocus);
      inputTel.addEventListener('blur', callbackBlur);
      inputTel.setAttribute('data-testid', 'phone-number-input');
    }

    if (countrySelector instanceof HTMLElement) {
      countrySelector.addEventListener('focus', callbackFocus);
      countrySelector.addEventListener('blur', callbackBlur);
    }
  }
};

const callbackFocus = (): void => {
  if (phoneField.value instanceof HTMLDivElement) {
    phoneField.value.classList.add('input-focus');
  }
};

const callbackBlur = (): void => {
  if (phoneField.value instanceof HTMLDivElement) {
    phoneField.value.classList.remove('input-focus');
  }
};

const updateInput = (e: PhoneFieldEvent): void => {
  emit('input', {
    phone_number: e.phoneNumber ?? '',
    formatted_number: e.formattedNumber ?? '',
    country_code: e.phoneNumber ? e.countryCallingCode : '',
    country_iso_code: e.countryCode,
  });

  let isValid = e.isValid;
  if (proxy?.$parent) {
    if (e.countryCode === 'AU' && !e.isValid) {
      // Some AU numbers on live clients are not passing vue-phone-number-input's internal validation.
      // This package is no longer being supported and needs to be replaced with an up to date solution.
      // For now, just validate AU numbers based on length - they should be 12 characters long including the international prefix.
      isValid = e.e164?.length === 12;
    }
    // @ts-ignore - TS doesn't recognize phoneValid could be on the parent
    proxy.$parent.phoneValid = isValid;
  }

  placeholder.value = e.countryCode === 'US'
    ? US_PHONE_DEFAULT_FORMAT
    : PHONE_DEFAULT_FORMAT;
}

const onPressEnter = (e: KeyboardEvent) => {
  // When pressing the return key while the country selector is open,
  // prevent the form from submitting.
  if (phoneField.value instanceof HTMLDivElement) {
    const countrySelectorOpen =
      phoneField.value.querySelector('.has-list-open') !== null;

    if (countrySelectorOpen) {
      e.preventDefault();
    }
  }
};

/**
 * Looking through the vuelidate error object to find the validation method that has been flags,
 * we then use the key to load the property of the same name from the list of customer error messages.
 * If no message matches, use the default one.
 * @returns The property key that failed teh validation
 */
const getErrorState = (): string => {
  if (isErrorExist.value) {
    const {
      onlyNumbers = true,
      minLength = true,
      maxLength = true,
      required = true,
    } = props.error as VuelidateState;

    if (!onlyNumbers) {
      return 'onlyNumbers';
    }

    if (!minLength) {
      return 'minLength';
    }

    if (!maxLength) {
      return 'maxLength';
    }

    if (!required) {
      return 'required';
    }
  }

  return 'phoneInvalid';
};

const getErrorMessage = (errorName: string): TranslateResult => {
  return props.customErrorMessages[errorName];
};

onMounted(() => {
  //set phoneValid as true allows to wait until get value.phone_number and check if this is valid
  // @ts-ignore - TS doesn't recognize phoneValid could be on the parent
  proxy.$parent.phoneValid = true;
  customiseVuePhoneInputLibrary();

  if (airportCountryCode.value === 'US') {
    placeholder.value = US_PHONE_DEFAULT_FORMAT;
  } else {
    placeholder.value = PHONE_DEFAULT_FORMAT;
  }
});

onBeforeUnmount(() => {
  if (phoneField.value instanceof HTMLDivElement) {
    const inputTel = phoneField.value.querySelector('.input-tel input');
    const countrySelector = phoneField.value.querySelector(
      '.country-selector input'
    );

    if (inputTel instanceof HTMLElement) {
      inputTel.removeEventListener('focus', callbackFocus);
      inputTel.removeEventListener('blur', callbackBlur);
    }

    if (countrySelector instanceof HTMLElement) {
      countrySelector.removeEventListener('focus', callbackFocus);
      countrySelector.removeEventListener('blur', callbackBlur);
    }
  }
});

expose({
  airportCountryCode,
  placeholder,
})

return { __sfc: true,props, emit, instance, proxy, placeholder, phoneField, airportCountryCode, isErrorExist, customErrorMessage, errorMessage, customiseVuePhoneInputLibrary, callbackFocus, callbackBlur, updateInput, onPressEnter, getErrorState, getErrorMessage, VuePhoneNumberInput }
}

})